package com.xtream.composeapp.presentation.ui.tvchannels

import androidx.compose.foundation.focusGroup
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.BrokenImage
import androidx.compose.material.icons.rounded.Tv
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.focusRestorer
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import coil3.compose.ConstraintsSizeResolver
import coil3.compose.rememberAsyncImagePainter
import coil3.compose.rememberConstraintsSizeResolver
import com.xtream.composeapp.presentation.widget.EqualizerLottie
import com.xtream.composeapp.viewmodel.livetv.StreamControlViewModel
import com.xtream.data_kmp.dto.LiveCategory
import com.xtream.data_kmp.dto.LiveStream
import com.xtream.sharedResources.resources.Res
import com.xtream.sharedResources.resources.tv_channels
import com.xtream.ui.ui.widget.FocusPositioner
import com.xtream.ui.ui.widget.TvButton
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource


@Composable
fun TvChannelsTvList(
    streamControlViewModel: StreamControlViewModel,
    modifier: Modifier = Modifier,
    requestFocusOnStart: Boolean = false,
    enableReorder:Boolean=false,
    streamState: LiveStream?,
    isFavorite: Boolean,
    listFocusRequester: FocusRequester = remember { FocusRequester() },
    playingCategory: LiveCategory?,

    isFullScreen: Boolean = false,
    onSameItemClicked: () -> Unit,
) {
    val tvChannelsUiState: List<LiveStream>? by streamControlViewModel.liveStreamsFlow.collectAsStateWithLifecycle()
    val selectedCategory by streamControlViewModel.selectedTvGroup.collectAsStateWithLifecycle()

    val scope = rememberCoroutineScope()
    val density = LocalDensity.current

    val allStreams = tvChannelsUiState ?: emptyList()

    // ── One FocusRequester per stream ─────────────────────────────────────────
    val focusRequesters = remember(allStreams) {
        allStreams.associate { stream ->
            (stream.streamId ?: stream.name ?: "") to FocusRequester()
        }
    }

    // ── Restore target: playing stream when category matches, else first item ─
    val restoreTargetKey by remember(streamState, selectedCategory, playingCategory, allStreams) {
        derivedStateOf {
            // FIX: Verify BOTH that the stream exists AND we are in the playing category
            val isCorrectCategory = playingCategory?.categoryId == selectedCategory?.categoryId

            val target = if (isCorrectCategory) {
                streamState
            } else {
                allStreams.firstOrNull()
            }
            target?.let { it.streamId ?: it.name ?: "" }
        }
    }

    val selectedIndex = remember(allStreams, streamState, selectedCategory) {
        val isCorrectCategory = playingCategory?.categoryId == selectedCategory?.categoryId
        if (isCorrectCategory) {
            allStreams.indexOfFirst { it.streamId == streamState?.streamId }.coerceAtLeast(0)
        } else {
            0
        }
    }

    val tvChannelsListState = rememberLazyListState(selectedIndex, 0)

    // ── Edge-wrap state (D-pad up from top / down from bottom) ────────────────
    var isAtTop by remember { mutableStateOf(false) }
    var isAtEnd by remember { mutableStateOf(false) }
    val screenHeightDp = with(density) {
        LocalWindowInfo.current.containerSize.height.toDp()
    }

// 1. Remember the static item height in pixels
    val itemHeightPx = with(density) { 60.dp.toPx() }

// 2. Derive the center offset from the list's layout info
// This will automatically update if the list size changes
    val centerOffset by remember(itemHeightPx) {
        derivedStateOf {
            val layoutInfo = tvChannelsListState.layoutInfo
            val viewportHeight = layoutInfo.viewportEndOffset + layoutInfo.viewportStartOffset
            if (viewportHeight > 0) {
                ((viewportHeight - itemHeightPx) / 2).toInt()
            } else {
                0
            }
        }
    }

    // 3. Simplified scroll helper
    suspend fun scrollToSelected() {
        val index = if (streamControlViewModel.isSelectedCategoryPlaying)
            streamControlViewModel.selectedPosition.value
        else 0

        // val centerOffset = screenHeightDp.value.toInt()
        tvChannelsListState.scrollToItem(index.coerceAtLeast(0), -centerOffset)

    }

    // ── Focus helper ──────────────────────────────────────────────────────────
    fun getRestoreFocusRequester(): FocusRequester =
        focusRequesters[restoreTargetKey] ?: FocusRequester.Default

    // ── Scroll on category/list change ────────────────────────────────────────
    LaunchedEffect(selectedCategory, tvChannelsUiState) {
        scrollToSelected()
    }

    // ── Scroll when returning from fullscreen ─────────────────────────────────
    LaunchedEffect(streamState, isFullScreen) {
        if (isFullScreen) scrollToSelected()
    }

    // ── Focus restore when collapsing from fullscreen ─────────────────────────
    var isInitialLoad by remember { mutableStateOf(true) }
    LaunchedEffect(isFullScreen) {
        if (!isFullScreen) {
            if (isInitialLoad) {
                isInitialLoad = false
                return@LaunchedEffect
            }
            scrollToSelected()
            getRestoreFocusRequester().requestFocus()
        }
    }

    // ── Initial focus request ─────────────────────────────────────────────────
    LaunchedEffect(tvChannelsUiState) {
        if (requestFocusOnStart) {
            listFocusRequester.requestFocus()
        }
    }
    val sizeResolver: ConstraintsSizeResolver = rememberConstraintsSizeResolver()

    Scaffold(
        modifier = modifier,
        topBar = {
            @OptIn(ExperimentalMaterial3Api::class)
            CenterAlignedTopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.background
                ),
                title = { Text(stringResource(Res.string.tv_channels)) }
            )
        }
    ) { innerPadding ->

        FocusPositioner  (
            parentFraction = 0.5f,
            childFraction = 0.5f,
        ) {
            // ── NO key() wrapper here ─────────────────────────────────────────
            // key(selectedCategory?.categoryId) was remounting the entire
            // LazyColumn on every category switch, which dropped focus to
            // nowhere and let the Categories composable steal it back.
            // Items update in-place via stable keys in itemsIndexed instead.

            LazyColumn(
                state = tvChannelsListState,
                modifier = Modifier
                    .focusRequester(listFocusRequester)
                    .focusGroup()
                    // Lambda form: always resolves to the current restore target.
                    .focusRestorer(
                        focusRequesters[restoreTargetKey]
                            ?: FocusRequester.Default
                    )
                    // IMPORTANT_UNCOMMENT_IN_NEXT_UPDATES
                    // .focusProperties {
                    //     onEnter = { if (isFullScreen) cancelFocusChange() }
                    // }
                    .onKeyEvent { keyEvent ->
                        // Early return if it's a key release event
                        if (keyEvent.type != KeyEventType.KeyDown) return@onKeyEvent false

                        // 🚀 1. Swap with platform-agnostic key property
                        val (targetIndex, targetKey) = when (keyEvent.key) {
                            // 🚀 2. Replace Android TV specific codes with common Directional tokens
                            Key.DirectionDown ->
                                if (isAtEnd) {
                                    0 to (allStreams.firstOrNull()?.let { it.streamId ?: it.name ?: "" })
                                } else null to null

                            Key.DirectionUp ->
                                if (isAtTop) {
                                    allStreams.lastIndex to (allStreams.lastOrNull()?.let { it.streamId ?: it.name ?: "" })
                                } else null to null

                            else -> null to null
                        }

                        if (targetIndex != null && targetKey != null) {
                            scope.launch {
                                tvChannelsListState.scrollToItem(targetIndex)
                                focusRequesters[targetKey]?.requestFocus()
                            }
                            true
                        } else {
                            false
                        }
                    }
                    .fillMaxSize(),
                verticalArrangement = Arrangement.spacedBy(6.dp),
                contentPadding = PaddingValues(
                    top = innerPadding.calculateTopPadding() + 8.dp,
                    bottom = screenHeightDp/2,
                    start = 16.dp, end = 16.dp
                )
            ) {
                itemsIndexed(
                    allStreams,
                    // Stable keys: prevents full item rebind on recomposition
                    key = { _, stream -> stream.streamId.plus(selectedCategory?.categoryId?:0)?: stream.name ?: "" }
                ) { index, stream ->
                    val itemKey = stream.streamId ?: stream.name ?: ""
                    val itemFocusRequester = focusRequesters[itemKey]
                        ?: remember { FocusRequester() }

                    TvButton(
                        selected = stream.streamId == streamState?.streamId && playingCategory?.categoryId == selectedCategory?.categoryId ,
                        title = "${index + 1} | ${stream.name}",
                        modifier = Modifier
                            .fillMaxWidth()
                            .focusRequester(itemFocusRequester)
                            .onFocusChanged { focusState ->
                                // Track edge items for D-pad wrap-around
                                when (index) {
                                    0 -> isAtTop = focusState.isFocused
                                    allStreams.lastIndex -> isAtEnd = focusState.isFocused
                                }
                            },
                        leadingIcon =  rememberAsyncImagePainter(
                            model = stream.streamIcon,
                            placeholder = rememberVectorPainter(Icons.Rounded.Tv),
                            error = rememberVectorPainter(Icons.Rounded.BrokenImage)
                        ),
                        useIconTint = false,
                        fillParent = true,

                        contentPadding =  PaddingValues(
                            start = 4.dp,
                        ),
                        trailingContent = if (streamState?.streamId == stream.streamId) {
                            { EqualizerLottie() }
                        } else {
                            null
                        },
                        /* leadingPainter = rememberAsyncImagePainter(
                             model = ImageRequest.Builder(context)
                                 .data(stream.streamIcon)
                                 .size(sizeResolver)
                                 //.crossfade(true)
                                 //.allowHardware(false)  Still need this so the Drawable can be read as a Bitmap
                                 .build(),
                             placeholder = rememberVectorPainter(Icons.Rounded.Tv),
                             error = rememberVectorPainter(Icons.Rounded.BrokenImage)
                         ),
                      sizeResolver=sizeResolver,
                         trailingContent = {
                             if (stream.streamId == streamState?.streamId && streamControlViewModel.isSelectedCategoryPlaying)
                             EqualizerLottie()
                         },
                         leadingPainterSize = 36.dp,
                         headline = "${index + 1} | ${stream.name}",
                         onLongClick = {
                             streamControlViewModel.updateFavoriteStatus(stream) { added ->
                                 scope.launch {
                                     snackbarHostState.showSnackbar(
                                         message = if (added)
                                             context.getString(Res.string.added_to_favorites)
                                         else
                                             context.getString(Res.string.removed_from_favorites)
                                     )
                                 }
                             }
                         },*/
                        onClick = {
                            if (stream.streamId == streamState?.streamId && streamControlViewModel.isSelectedCategoryPlaying)
                                onSameItemClicked()
                            else
                                streamControlViewModel.emitSelectedTvChannel(index, stream)
                        }
                    )
                }
            }
        }
    }
}
